/** Every figure the product prints is formatted here, and nowhere else.
 *
 *  `Intl` and `toLocaleString` fall back to the browser language when no locale
 *  is named, so "401.641" could read as 401 thousand. Every formatter names
 *  `en-US` explicitly. Components do not call `toLocaleString` or `Intl.*`
 *  directly; add a helper here instead. */

/** "USD 123,120,492", not "$123,120,492": a bare $ is ambiguous across the
 *  currencies the platform reports in. For headline figures use `usdCompact`. */
export function usd(value: number | null | undefined): string {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    currencyDisplay: "code",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/** "USD 123.1M". Headline amounts. Pair with `usd()` in a title attribute when
 *  the exact figure has to be reachable. */
export function usdCompact(value: number | null | undefined): string {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    currencyDisplay: "code",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}

/** "3.8M". Counts at headline size. */
export function numCompact(value: number | null | undefined): string {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}

export function pct(value: number | null | undefined, decimals = 1): string {
  if (value == null) return "—";
  return `${(value * 100).toFixed(decimals)}%`;
}

export function num(value: number | null | undefined, decimals = 2): string {
  if (value == null) return "—";
  return value.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

/** "4,730". Counts (loans, debtors, rows, installments): grouped, no decimals. */
export function int(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return value.toLocaleString("en-US", { maximumFractionDigits: 0 });
}

/** "1.2", "0.9583": up to `decimals` places, trailing zeros dropped (e.g. an FX
 *  rate). `num()` is the fixed-width counterpart. */
export function numMax(value: number | null | undefined, decimals: number): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return value.toLocaleString("en-US", { maximumFractionDigits: decimals });
}

/** "USD 1.9M", "USD 402K", "USD 401.64". The abbreviated money format for
 *  balances in cards and tables.
 *
 *  Between 1K and 10K one decimal is kept ("USD 1.5K"); above, none ("USD 402K").
 *  Below 1K the figure prints whole and that branch must name a locale (the
 *  others use locale-independent `toFixed`). Negatives read "-USD 2.5K". */
export function fmtAmt(v: number | null | undefined, currency: string): string {
  if (v == null || !Number.isFinite(v)) return "—";
  const abs = Math.abs(v);
  const sign = v < 0 ? "-" : "";
  if (abs >= 1e9) return `${sign}${currency} ${(abs / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${sign}${currency} ${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 10e3) return `${sign}${currency} ${(abs / 1e3).toFixed(0)}K`;
  if (abs >= 1e3) return `${sign}${currency} ${(abs / 1e3).toFixed(1)}K`;
  return `${sign}${currency} ${abs.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
}

/** "PEN 2,606,180.21". Exact, with cents, for figures checked against the
 *  facility cockpits. Null is a dash; the caller explains why. */
export function money(value: number | null | undefined, currency: string): string {
  if (value == null || !Number.isFinite(value)) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      currencyDisplay: "code",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      // `currencyDisplay: "code"` emits a non-breaking space, which does not
      // survive a copy into a spreadsheet; it is normalised to a plain space.
    }).format(value).replace(/\s/g, " ");
  } catch {
    // `Intl` throws on an unknown ISO code; group by hand and prefix the code.
    return `${currency} ${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
}

/** "USD 401,641". Exact, whole units; the tooltip form of `fmtAmt`. */
export function exactNative(value: number | null | undefined, currency: string): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${currency} ${value.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

/** "USD 402K". Compact money in a native currency, for dense rows. */
export function nativeAmount(value: number | null | undefined, currency: string): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${currency} ${COMPACT_NUM.format(value)}`;
}

/** Compact USD with the currency code, matching `usd()`. */
export const COMPACT_USD = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  currencyDisplay: "code",
  notation: "compact",
  maximumFractionDigits: 1,
});

export const COMPACT_NUM = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});

/* Dates.
 *
 *  Month and weekday names come from a table, not `Intl`: CLDR data differs by
 *  locale and release (`en-GB` writes "Sept", `en-US` "Sep"). Day first and 24h
 *  throughout. Always absolute, never relative ("3 weeks ago" cannot be
 *  reconciled against a statement); `timeRemaining` only sits beside an absolute
 *  deadline. */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const pad2 = (n: number) => String(n).padStart(2, "0");

export type DateLike = string | Date | null | undefined;

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** A date-only string is a calendar day, read off the string itself: parsed as
 *  an instant it lands on UTC midnight and shows the previous day west of
 *  Greenwich. A full timestamp is read in local time. */
function parts(v: DateLike) {
  if (v == null || v === "") return null;
  if (typeof v === "string" && DATE_ONLY.test(v)) {
    const [y, m, d] = v.split("-").map(Number);
    return { y, m: m - 1, d, hh: 0, mi: 0, wd: new Date(Date.UTC(y, m - 1, d)).getUTCDay() };
  }
  const t = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(t.getTime())) return null;
  return { y: t.getFullYear(), m: t.getMonth(), d: t.getDate(), hh: t.getHours(), mi: t.getMinutes(), wd: t.getDay() };
}

/** Today as a local calendar day, "YYYY-MM-DD": `toISOString` would give the
 *  UTC day, which is tomorrow late in the evening west of Greenwich. */
export function localToday(now: Date = new Date()): string {
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
}

/** "24 Sep 2026". The default for any date a reader has to check. */
export function formatDate(v: DateLike): string {
  const p = parts(v);
  return p ? `${pad2(p.d)} ${MONTHS[p.m]} ${p.y}` : "—";
}

/** "24 Sep 2026 16:32". Absolute timestamp, always with the clock. */
export function formatDateTime(v: DateLike): string {
  const p = parts(v);
  return p ? `${pad2(p.d)} ${MONTHS[p.m]} ${p.y} ${pad2(p.hh)}:${pad2(p.mi)}` : "—";
}

/** "24 Sep". Inside a column or an axis, where the year is already given. */
export function formatDayMonth(v: DateLike): string {
  const p = parts(v);
  return p ? `${pad2(p.d)} ${MONTHS[p.m]}` : "—";
}

/** "24 Sep 16:32". A recent event, where the year would be noise. */
export function formatDayMonthTime(v: DateLike): string {
  const p = parts(v);
  return p ? `${pad2(p.d)} ${MONTHS[p.m]} ${pad2(p.hh)}:${pad2(p.mi)}` : "—";
}

/** "Thu, 24 Sep". Chart tooltips, where the weekday explains a weekly shape. */
export function formatWeekdayDayMonth(v: DateLike): string {
  const p = parts(v);
  return p ? `${WEEKDAYS[p.wd]}, ${pad2(p.d)} ${MONTHS[p.m]}` : "—";
}

/** "Sep 2026". A reporting period, which has no day. */
export function formatMonthYear(v: DateLike): string {
  const p = parts(v);
  return p ? `${MONTHS[p.m]} ${p.y}` : "—";
}

/** "Sep ’26". A monthly axis, where every tick repeats the century. */
export function formatMonthYearShort(v: DateLike): string {
  const p = parts(v);
  return p ? `${MONTHS[p.m]} ’${pad2(p.y % 100)}` : "—";
}

/** "US Dollar" for "USD", pinned to English. Null for a code `Intl` does not
 *  know. */
const CURRENCY_NAMES = new Intl.DisplayNames(["en"], { type: "currency" });

export function currencyName(code: string): string | null {
  try {
    const name = CURRENCY_NAMES.of(code);
    return name && name !== code ? name : null;
  } catch {
    return null;
  }
}

/** "16:32". The clock alone, next to a date already on screen. */
export function formatTime(v: DateLike): string {
  const p = parts(v);
  return p ? `${pad2(p.hh)}:${pad2(p.mi)}` : "—";
}

/** Time left until a deadline: "2d 4h", "4h 12m", "35m". Null when past (caller
 *  renders OVERDUE) or invalid. Shown next to the absolute timestamp. */
export function timeRemaining(iso: string | null | undefined, now: Date = new Date()): string | null {
  if (!iso) return null;
  const deadline = new Date(iso);
  if (isNaN(deadline.getTime())) return null;
  const ms = deadline.getTime() - now.getTime();
  if (ms <= 0) return null;
  const minutes = Math.floor(ms / 60_000);
  const days = Math.floor(minutes / 1_440);
  const hours = Math.floor((minutes % 1_440) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  return `${minutes}m`;
}

export function statusColor(status: string | null | undefined): string {
  switch (status?.toUpperCase()) {
    case "COMPLIANT": return "status-compliant";
    case "WARNING": return "status-warning";
    case "BREACHED": return "status-breached";
    default: return "bg-gray-100 text-gray-600";
  }
}
